import React from "react";
import { Button, Typography } from "@material-ui/core";

// Same visual design as the Proforma Invoice auto-sent over WhatsApp by the
// AI agent (manijment-api/services/ai-service/aiPiPdf.js, mirrored client-side
// in buyume-crm/src/menu-items/Dashboard/MyChats.js downloadAiOrderPI) —
// header, company/bill-to box, line-item table, totals block, footer note.
// Adapted here for a real (converted) BulkOrder, which can carry multiple
// products, instead of the AI's single-line-item chat order.

function amountInWords(amount) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function convert(n) {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + convert(n % 100);
    if (n < 100000) return convert(Math.floor(n / 1000)) + 'Thousand ' + convert(n % 1000);
    if (n < 10000000) return convert(Math.floor(n / 100000)) + 'Lakh ' + convert(n % 100000);
    return convert(Math.floor(n / 10000000)) + 'Crore ' + convert(n % 10000000);
  }
  const n = Math.round(amount);
  if (!n) return 'Rupees Zero Only';
  return 'Rupees ' + convert(n).trim().replace(/\s+/g, ' ') + ' Only';
}

async function downloadBulkOrderPI(order) {
  const jsPDF = (await import("jspdf")).default;

  const piDate = order?.createdAt ? new Date(order.createdAt) : new Date();
  const isoDate = `${piDate.getFullYear()}-${String(piDate.getMonth() + 1).padStart(2, "0")}-${String(piDate.getDate()).padStart(2, "0")}`;
  const dateStr = piDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const piNum = `FL-PI-${isoDate.replace(/-/g, "")}-${String(order?.numericOrderId || order?._id || "").slice(-5)}`;
  const fmtAmt = (n) => (n == null ? "—" : "Rs." + Math.round(n).toLocaleString("en-IN"));

  const products = order?.products || [];
  const shippingCharge = order?.shippingCost || 0;
  const orderValue = order?.totalPayableAmount != null
    ? order.totalPayableAmount
    : products.reduce((acc, p) => acc + (p.price || 0) * (p.quantity || 0), 0) + shippingCharge;
  const advancePaid = order?.prepaidCollectedAmount || 0;
  const payOnDelivery = order?.remainingAmountForCod != null ? order.remainingAmountForCod : orderValue - advancePaid;

  const addr = order?.address || {};
  const addrLines = [addr.address1, addr.address2, addr.landmark].filter(Boolean).join(", ");
  const addrTail = [addr.city, addr.postalCode].filter(Boolean).join(" — ");

  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const PW = pdf.internal.pageSize.getWidth();
  const PH = pdf.internal.pageSize.getHeight();
  const ML = 36, MR = 36, CW = PW - ML - MR;
  let y = ML;

  const sf = (style, size) => { pdf.setFont("helvetica", style); pdf.setFontSize(size); };
  const sc = (r, g, b) => pdf.setTextColor(r, g, b);
  const sdc = (r, g, b) => pdf.setDrawColor(r, g, b);
  const sfc = (r, g, b) => pdf.setFillColor(r, g, b);
  const txt = (t, x, yy, opts) => pdf.text(String(t), x, yy, opts || {});
  const G = { text: [80, 80, 80], muted: [155, 155, 155], label: [130, 130, 130] };
  const border = () => sdc(224, 224, 224);

  sf("bold", 20); sc(25, 75, 145);
  txt("PROFORMA INVOICE", PW / 2, y + 20, { align: "center" });
  sf("normal", 8); sc(90, 90, 90);
  txt("Email: info@floridabeautylabs.com", PW / 2, y + 33, { align: "center" });
  txt(`PI No: ${piNum}   |   Date: ${dateStr}`, PW / 2, y + 46, { align: "center" });
  y += 60;

  const halfW = CW / 2, BOX_H = 88;
  sfc(242, 242, 242); border();
  pdf.rect(ML, y, CW, BOX_H, "FD");
  sdc(210, 210, 210); pdf.line(ML + halfW, y, ML + halfW, y + BOX_H);
  border(); pdf.rect(ML, y, CW, BOX_H, "S");

  sf("bold", 9); sc(...G.text);
  txt("Florida Beauty Labs Private Limited", ML + 10, y + 14);
  sf("normal", 8); sc(...G.text);
  txt("E-17, 3rd Floor, Main Market", ML + 10, y + 26);
  txt("South Extension Part II", ML + 10, y + 38);
  txt("New Delhi Delhi 110049", ML + 10, y + 50);
  sf("bold", 8); sc(...G.label);
  txt("GSTIN: 07AAFCF6219A1Z1", ML + 10, y + 64);

  const rx = ML + halfW + 10, vx = rx + 56;
  sf("bold", 7); sc(...G.label);
  txt("BILL TO", rx, y + 11);
  const billRow = (label, val, ly) => {
    sf("normal", 8); sc(...G.label); txt(label, rx, ly);
    sf("normal", 8); sc(...G.text); txt(String(val), vx, ly);
  };
  billRow("Name", addr.name || "—", y + 27);
  billRow("Phone", addr.phoneNumber || "—", y + 41);
  sf("normal", 7.5); sc(...G.muted);
  const addrDisplay = [addrLines, addrTail].filter(Boolean).join(" — ") || "—";
  const addrWrapped = pdf.splitTextToSize(addrDisplay, halfW - 56 - 10);
  addrWrapped.slice(0, 2).forEach((line, li) => txt(line, vx, y + 55 + li * 10));
  y += BOX_H;

  const cols = [
    { label: "#", w: 24, align: "center" },
    { label: "Product", w: 260, align: "left" },
    { label: "Qty", w: 60, align: "center" },
    { label: "Rate", w: 80, align: "right" },
    { label: "Amount", w: CW - 24 - 260 - 60 - 80, align: "right" },
  ];
  let cx = ML;
  cols.forEach((c) => { c.x = cx; cx += c.w; });

  sfc(215, 215, 215); border();
  pdf.rect(ML, y, CW, 22, "FD");
  sf("bold", 7.5); sc(60, 60, 60);
  cols.forEach((c) => {
    const tx_ = c.align === "right" ? c.x + c.w - 4 : c.align === "center" ? c.x + c.w / 2 : c.x + 4;
    txt(c.label, tx_, y + 15, { align: c.align });
  });
  y += 22;

  products.forEach((p, idx) => {
    if (y > PH - 140) { pdf.addPage(); y = ML; }
    sf("normal", 8.5);
    const nameLines = pdf.splitTextToSize(p.name || "—", cols[1].w - 8);
    const rowH = Math.max(20, nameLines.length * 13 + 8);
    sfc(idx % 2 === 0 ? 246 : 255, idx % 2 === 0 ? 246 : 255, idx % 2 === 0 ? 246 : 255);
    pdf.rect(ML, y, CW, rowH, "F");
    const midY = y + rowH / 2 + 3, nameY0 = nameLines.length === 1 ? midY : y + 13;
    sc(...G.muted); txt(String(idx + 1), cols[0].x + cols[0].w / 2, midY, { align: "center" });
    sc(...G.text); nameLines.forEach((line, li) => txt(line, cols[1].x + 4, nameY0 + li * 13));
    txt(String(p.quantity || 0), cols[2].x + cols[2].w / 2, midY, { align: "center" });
    sf("bold", 8.5); txt(fmtAmt(p.price), cols[3].x + cols[3].w - 4, midY, { align: "right" });
    txt(fmtAmt((p.price || 0) * (p.quantity || 0)), cols[4].x + cols[4].w - 4, midY, { align: "right" });
    border(); pdf.line(ML, y + rowH, PW - MR, y + rowH);
    y += rowH;
  });
  y += 10;

  if (y > PH - 160) { pdf.addPage(); y = ML; }

  const SLX = PW - MR - 200;
  const sumRow = (label, val, bold = false) => {
    const rh = bold ? 26 : 20;
    sfc(bold ? 215 : 246, bold ? 215 : 246, bold ? 215 : 246); border();
    pdf.rect(ML, y, CW, rh, "FD");
    sdc(210, 210, 210); pdf.line(SLX, y, SLX, y + rh);
    border(); pdf.line(ML, y + rh, PW - MR, y + rh);
    sf(bold ? "bold" : "normal", bold ? 10 : 9);
    sc(...(bold ? [60, 60, 60] : G.label));
    txt(label, SLX + 10, y + (bold ? 18 : 14));
    sc(...(bold ? [60, 60, 60] : G.text));
    txt(val, PW - MR - 8, y + (bold ? 18 : 14), { align: "right" });
    y += rh;
  };
  if (shippingCharge) sumRow("Delivery Charges", fmtAmt(shippingCharge));
  sumRow("Order Value", fmtAmt(orderValue));
  if (advancePaid) {
    sumRow("Advance Paid", fmtAmt(advancePaid));
    sumRow("Pay on Delivery", fmtAmt(payOnDelivery), true);
  } else {
    sumRow("Pay on Delivery", fmtAmt(orderValue), true);
  }
  y += 10;

  sf("normal", 7.5); sc(...G.muted);
  txt(`Amount in words: ${amountInWords(payOnDelivery ?? orderValue)}`, ML, y);
  y += 18;

  border(); pdf.line(ML, y, PW - MR, y); y += 8;
  sf("normal", 7); sc(...G.muted);
  txt("This is a system-generated proforma invoice. Please confirm your order on WhatsApp.", PW / 2, y, { align: "center" });

  pdf.save(`Proforma_Invoice_${order?.numericOrderId || order?._id || ""}.pdf`);
}

function PerformaInvoice({ singleOrder }) {
  const products = singleOrder?.products || [];
  const shippingCharge = singleOrder?.shippingCost || 0;
  const orderValue = singleOrder?.totalPayableAmount != null
    ? singleOrder.totalPayableAmount
    : products.reduce((acc, p) => acc + (p.price || 0) * (p.quantity || 0), 0) + shippingCharge;
  const advancePaid = singleOrder?.prepaidCollectedAmount || 0;
  const payOnDelivery = singleOrder?.remainingAmountForCod != null ? singleOrder.remainingAmountForCod : orderValue - advancePaid;
  const addr = singleOrder?.address || {};

  return (
    <div style={{ position: "relative" }}>
      <div style={{ padding: "20px", maxWidth: "800px", margin: "auto", border: "1px solid #e0e0e0", borderRadius: 6 }}>
        <h1 style={{ textAlign: "center", color: "#194b91", marginBottom: 4 }}>PROFORMA INVOICE</h1>
        <p style={{ textAlign: "center", color: "#5a5a5a", fontSize: 12, margin: "2px 0" }}>
          PI No: FL-PI-{singleOrder?.numericOrderId} &nbsp;|&nbsp; Date:{" "}
          {singleOrder?.createdAt ? new Date(singleOrder.createdAt).toLocaleDateString("en-IN") : "—"}
        </p>

        <div style={{ display: "flex", border: "1px solid #e0e0e0", marginTop: 16 }}>
          <div style={{ flex: 1, padding: 10, borderRight: "1px solid #e0e0e0" }}>
            <strong>Florida Beauty Labs Private Limited</strong>
            <div style={{ fontSize: 12, color: "#505050" }}>
              E-17, 3rd Floor, Main Market<br />
              South Extension Part II<br />
              New Delhi Delhi 110049
            </div>
            <div style={{ fontSize: 11, color: "#828282", marginTop: 6 }}>GSTIN: 07AAFCF6219A1Z1</div>
          </div>
          <div style={{ flex: 1, padding: 10 }}>
            <strong style={{ fontSize: 11, color: "#828282" }}>BILL TO</strong>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <div>Name: {addr.name || "—"}</div>
              <div>Phone: {addr.phoneNumber || "—"}</div>
              <div style={{ color: "#9b9b9b", fontSize: 11, marginTop: 2 }}>
                {[addr.address1, addr.address2, addr.landmark, addr.city, addr.postalCode].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
          <thead>
            <tr style={{ background: "#d7d7d7" }}>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>#</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Product</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Qty</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Rate</th>
              <th style={{ border: "1px solid #ddd", padding: "8px" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, idx) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{idx + 1}</td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{product.name}</td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{product.quantity}</td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>₹{product.price}</td>
                <td style={{ border: "1px solid #ddd", padding: "8px" }}>₹{(product.price || 0) * (product.quantity || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {shippingCharge ? (
            <Typography variant="body2">Delivery Charges: ₹{shippingCharge}</Typography>
          ) : null}
          <Typography variant="body2">Order Value: ₹{orderValue}</Typography>
          {advancePaid ? (
            <>
              <Typography variant="body2">Advance Paid: ₹{advancePaid}</Typography>
              <Typography variant="subtitle1" style={{ color: "#194b91" }}>Pay on Delivery: ₹{payOnDelivery}</Typography>
            </>
          ) : (
            <Typography variant="subtitle1" style={{ color: "#194b91" }}>Pay on Delivery: ₹{orderValue}</Typography>
          )}
        </div>

        <p style={{ fontSize: 11, color: "#9b9b9b", marginTop: 12, fontStyle: "italic" }}>
          This is a system-generated proforma invoice. Please confirm your order on WhatsApp.
        </p>
      </div>

      <div style={{ textAlign: "center", marginTop: "20px", position: "absolute", right: 10, top: 10 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => downloadBulkOrderPI(singleOrder)}
        >
          Download PDF
        </Button>
      </div>
    </div>
  );
}

export default PerformaInvoice;
